// Mock data for the Workflow detail mockup (/app-screen-mockups/workflow-show).
// This screen showcases a single workflow: the inputs it requires, its steps
// (viewable + editable), and the history of runs — including a live run the
// reviewer can trigger.
export type RunStatus = 'success' | 'failed' | 'running';
export type TraceStatus = 'success' | 'failed' | 'running' | 'pending' | 'skipped';
export type InputType = 'URL' | 'Text' | 'Long text' | 'Number' | 'Date' | 'Notion database' | 'Select';
export const INPUT_TYPES: InputType[] = ['URL', 'Text', 'Long text', 'Number', 'Date', 'Notion database', 'Select'];
export interface WorkflowInput {
  id: string;
  /** Machine name the steps reference, e.g. {{tweet_url}}. */
  key: string;
  label: string;
  type: InputType;
  required: boolean;
  hint: string;
}
export interface WorkflowStep {
  id: string;
  title: string;
  body: string;
}
export interface StepTrace {
  stepId: string;
  status: TraceStatus;
  duration: string;
  detail: string;
  log: string[];
}
export interface WorkflowRun {
  id: string;
  trigger: string;
  actor: string;
  startedAt: string;
  relative: string;
  duration: string;
  status: RunStatus;
  /** Values supplied for the workflow's inputs, keyed by input key. */
  inputValues: Record<string, string>;
  output?: string;
  credits: number;
  failureReason?: string;
  traces: StepTrace[];
}
export function logoUrl(domain: string, size = 64) {
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=${size}`;
}
export const WORKFLOW = {
  name: 'Scrape Tweet to Notion',
  description: 'Takes a Twitter/X URL, scrapes the tweet content using available credentials or fallback scrapers, and inserts it into the specified Notion table.',
  triggerLabel: 'Manual + when a link is shared in #content-ideas',
  owner: 'Tony Ennis',
  updated: 'Updated 2 days ago',
  successRate: '94%',
  avgDuration: '41s',
  totalRuns: 68
};
export const INITIAL_INPUTS: WorkflowInput[] = [{
  id: 'in-1',
  key: 'tweet_url',
  label: 'Tweet URL',
  type: 'URL',
  required: true,
  hint: 'The Twitter/X post to capture.'
}, {
  id: 'in-2',
  key: 'notion_database',
  label: 'Destination table',
  type: 'Notion database',
  required: true,
  hint: 'Defaults to Content Ideas.'
}, {
  id: 'in-3',
  key: 'tags',
  label: 'Tags',
  type: 'Text',
  required: false,
  hint: 'Comma-separated labels added to the new row.'
}];
export const DEFAULT_INPUT_VALUES: Record<string, string> = {
  tweet_url: 'https://x.com/gigaai/status/1892740183746',
  notion_database: 'Content Ideas',
  tags: 'launch, social'
};
export const OUTPUT_URL = 'notion.so/Content-Ideas/Tweet-a1f9c2';
export const INITIAL_STEPS: WorkflowStep[] = [{
  id: 'step-1',
  title: 'Parse the Twitter URL and check credentials',
  body: "Take {{tweet_url}} as input and extract the tweet ID. Then check the user's vault credentials for an active X/Twitter connection — prefer the OAuth credential (x-user-2) for API access, or the browser_session as a fallback. Also confirm the Notion MCP credential (mcp-notion-claude-2026-05-12) is available. Report what credentials will be used before proceeding."
}, {
  id: 'step-2',
  title: 'Scrape the tweet content',
  body: 'Scrape the full tweet content including: author handle, display name, tweet text, timestamp, like count, reply count, retweet count, and any media links or quoted tweet content. If an X API credential is available, use it via the HTTP proxy to call the Twitter API v2 tweet lookup endpoint. If the API approach fails or no API credential works, fall back to a scraping tool.'
}, {
  id: 'step-3',
  title: 'Insert into Notion table',
  body: 'Take the scraped tweet data and insert it into {{notion_database}}. Using the Notion MCP credential, first fetch the database schema, then map the scraped fields (author, text, URL, timestamp, engagement counts) plus {{tags}} to the appropriate Notion columns. Create a new row with all the mapped data and return a link to the new page.'
}];
/** Per-step copy used while a live run is streaming. */
export const LIVE_STEP_DETAIL = ['Reading vault credentials…', 'Calling Twitter API v2 tweet lookup…', 'Mapping fields onto the Notion schema…'];
export const LIVE_STEP_DURATION = ['3.1s', '18.4s', '11.2s'];
export const LIVE_STEP_LOG = [['Extracted tweet ID 1892740183746', 'Using OAuth credential x-user-2', 'Notion MCP credential verified'], ['GET /2/tweets/1892740183746 → 200 OK', 'Captured 9 fields incl. 2 media links'], ['Fetched schema — 7 properties', 'Created row “Tweet — @gigaai”']];
function trace(stepId: string, status: TraceStatus, duration: string, detail: string, log: string[]): StepTrace {
  return {
    stepId,
    status,
    duration,
    detail,
    log
  };
}
export const INITIAL_RUNS: WorkflowRun[] = [{
  id: 'run-104',
  trigger: 'Slack link',
  actor: '#content-ideas',
  startedAt: 'Today, 2:14 PM',
  relative: '38 min ago',
  duration: '36s',
  status: 'success',
  inputValues: {
    tweet_url: 'https://x.com/lennysan/status/1892611094722',
    notion_database: 'Content Ideas',
    tags: 'newsletter'
  },
  output: OUTPUT_URL,
  credits: 11,
  traces: [trace('step-1', 'success', '2.8s', 'OAuth credential x-user-2 selected', ['Extracted tweet ID 1892611094722', 'Notion MCP credential verified']), trace('step-2', 'success', '16.9s', 'Captured 9 fields from Twitter API v2', ['GET /2/tweets/1892611094722 → 200 OK', '1 quoted tweet included']), trace('step-3', 'success', '10.4s', 'Created row “Tweet — @lennysan”', ['Fetched schema — 7 properties', 'Row created successfully'])]
}, {
  id: 'run-103',
  trigger: 'Manual',
  actor: 'Tony Ennis',
  startedAt: 'Today, 9:02 AM',
  relative: '5 hours ago',
  duration: '1m 12s',
  status: 'failed',
  inputValues: {
    tweet_url: 'https://x.com/private_acct/status/1891004556210',
    notion_database: 'Content Ideas',
    tags: ''
  },
  credits: 6,
  failureReason: 'Tweet is from a protected account — the API returned 403 and the browser session fallback hit a login wall.',
  traces: [trace('step-1', 'success', '3.0s', 'Fell back to browser_session credential', ['OAuth credential x-user-2 rate limited', 'Using browser_session fallback']), trace('step-2', 'failed', '1m 04s', 'Twitter API returned 403 Forbidden', ['GET /2/tweets/1891004556210 → 403', 'Fallback scraper hit login wall', 'Aborting run']), trace('step-3', 'skipped', '—', 'Skipped — upstream step failed', [])]
}, {
  id: 'run-102',
  trigger: 'Slack link',
  actor: '#content-ideas',
  startedAt: 'Yesterday, 4:47 PM',
  relative: '1 day ago',
  duration: '44s',
  status: 'success',
  inputValues: {
    tweet_url: 'https://x.com/stripe/status/1890338822104',
    notion_database: 'Content Ideas',
    tags: 'payments, competitor'
  },
  output: 'notion.so/Content-Ideas/Tweet-77be10',
  credits: 12,
  traces: [trace('step-1', 'success', '3.4s', 'OAuth credential x-user-2 selected', ['Extracted tweet ID 1890338822104']), trace('step-2', 'success', '22.1s', 'Captured 9 fields + 1 media link', ['GET /2/tweets/1890338822104 → 200 OK']), trace('step-3', 'success', '18.5s', 'Created row “Tweet — @stripe”', ['Row created successfully'])]
}, {
  id: 'run-101',
  trigger: 'Manual',
  actor: 'Sarah Chen',
  startedAt: 'Mon, 11:20 AM',
  relative: '3 days ago',
  duration: '39s',
  status: 'success',
  inputValues: {
    tweet_url: 'https://x.com/notionhq/status/1889120044981',
    notion_database: 'Product Research',
    tags: 'notion'
  },
  output: 'notion.so/Content-Ideas/Tweet-2c04ab',
  credits: 11,
  traces: [trace('step-1', 'success', '2.6s', 'OAuth credential x-user-2 selected', ['Extracted tweet ID 1889120044981']), trace('step-2', 'success', '17.2s', 'Captured 8 fields', ['GET /2/tweets/1889120044981 → 200 OK']), trace('step-3', 'success', '19.0s', 'Created row “Tweet — @NotionHQ”', ['Row created successfully'])]
}];